import { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchOrder,
  OrderResponse,
  updateOrder,
  type UpdateOrderPayload,
} from '../../../api/orders';
import Notification from '../../../components/midone/components/Base/Notification';
import type { NotificationElement } from '../../../components/midone/components/Base/Notification';
import SearchableDropdown, {
  type SearchableOption,
} from '../../../components/SearchableDropdown';
import { getAiHealthScoreStyles } from '../../../utils/aiUtils';
import { fetchUsers } from '../../../api/users';
import { fetchCurrentUser, type UserProfile } from '../../../api/auth';

interface OrderManagerApprovalProps {
  orderId: string | null;
  showHeader?: boolean;
  onBackToOrders?: () => void;
  onReturnToIntake?: (orderId: string | null) => void;
  onCompleted?: (orderId: string) => void;
  lockApproverToCurrentUser?: boolean;
}

type ToastState = {
  id: number;
  title: string;
  message: string;
};

const TOAST_DURATION = 2500;

const formatDateTime = (value: string | null) => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleString();
};

const getUserDisplayName = (user?: UserProfile | null) => {
  if (!user) {
    return 'Unknown user';
  }

  const name = [user.firstName, user.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  if (name) {
    return name;
  }

  return user.username || 'Unknown user';
};

const formatUserLocations = (user?: UserProfile | null) => {
  const locs = user?.locations;
  if (!locs) return 'All locations';
  if (Array.isArray(locs) && locs.length === 0) return 'All locations';
  return locs.map((l) => l.name).join(', ');
};

const OrderManagerApproval: FC<OrderManagerApprovalProps> = ({
  orderId,
  showHeader = true,
  onCompleted,
  lockApproverToCurrentUser = false,
}) => {
  const [order, setOrder] = useState<OrderResponse | null>(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [managerApproved, setManagerApproved] = useState(false);
  const [managerNotes, setManagerNotes] = useState('');
  const [selectedManagerId, setSelectedManagerId] = useState<string | null>(null);
  const [selectedManager, setSelectedManager] = useState<UserProfile | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [userResults, setUserResults] = useState<UserProfile[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const toastRef = useRef<NotificationElement | null>(null);
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [aiModalOpen, setAiModalOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    fetchCurrentUser()
      .then((response) => {
        if (!isMounted) {
          return;
        }
        setCurrentUser(response.user);
        if (lockApproverToCurrentUser && response.user?.id) {
          setSelectedManagerId(response.user.id);
          setSelectedManager(response.user);
        }
      })
      .catch(() => {
        if (isMounted) {
          setCurrentUser(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!orderId) {
      setOrder(null);
      setManagerApproved(false);
      setManagerNotes('');
      setSelectedManagerId(null);
      setSelectedManager(null);
      setOrderError('Order ID not specified.');
      setLoadingOrder(false);
      return;
    }

    let isMounted = true;
    setLoadingOrder(true);
    setOrderError(null);

    fetchOrder(orderId)
      .then((data) => {
        if (!isMounted) {
          return;
        }

        setOrder(data);
        setManagerApproved(data.managerApproved ?? false);
        setManagerNotes(data.managerApprovalNotes ?? '');
        const initialManagerId = data.managerApprovedById ?? data.managerId ?? null;
        const initialManager = data.managerApprovedBy ?? data.manager ?? null;
        setSelectedManagerId(initialManagerId);
        setSelectedManager(initialManager);
        setSaveError(null);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }
        const message = error instanceof Error ? error.message : 'Failed to load order details.';
        setOrder(null);
        setManagerApproved(false);
        setManagerNotes('');
        setSelectedManagerId(null);
        setSelectedManager(null);
        setOrderError(message);
      })
      .finally(() => {
        if (isMounted) {
          setLoadingOrder(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [orderId]);

  useEffect(() => {
    if (!toast) {
      return;
    }

    toastRef.current?.showToast?.();

    const handle = window.setTimeout(() => {
      toastRef.current?.hideToast?.();
      setToast(null);
    }, TOAST_DURATION);

    return () => {
      window.clearTimeout(handle);
      toastRef.current?.hideToast?.();
    };
  }, [toast]);

  useEffect(() => {
    const trimmed = search.trim();

    if (trimmed.length < 2) {
      setDebouncedSearch('');
      setUserResults([]);
      setSearchError(null);
      setSearching(false);
      return;
    }

    setSearching(true);
    setSearchError(null);
    const handle = window.setTimeout(() => setDebouncedSearch(trimmed), 300);

    return () => {
      window.clearTimeout(handle);
    };
  }, [search]);

  useEffect(() => {
    if (!debouncedSearch) {
      return;
    }

    let isMounted = true;

    fetchUsers({ search: debouncedSearch, limit: 8, role: 'STORE_MANAGER' })
      .then((response) => {
        if (!isMounted) {
          return;
        }
        // Extra safeguard: filter client-side to STORE_MANAGER only
        const rows = (response.data ?? []).filter(
          (u) => (u.typeKey ?? '').toUpperCase() === 'STORE_MANAGER',
        );
        setUserResults(rows);
        setSearchError(null);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }
        const message = error instanceof Error ? error.message : 'Failed to search users.';
        setUserResults([]);
        setSearchError(message);
      })
      .finally(() => {
        if (isMounted) {
          setSearching(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [debouncedSearch]);

  useEffect(() => {
    if (!selectedManagerId) {
      return;
    }

    const match = userResults.find((user) => user.id === selectedManagerId);

    if (match) {
      setSelectedManager(match);
    }
  }, [selectedManagerId, userResults]);

  const options: SearchableOption[] = useMemo(
    () =>
      userResults.map((user) => ({
        value: user.id,
        label: getUserDisplayName(user),
        description: user.email ?? user.username,
      })),
    [userResults],
  );

  const normalizedNotes = useMemo(() => managerNotes.trim(), [managerNotes]);
  const initialNotes = useMemo(() => (order?.managerApprovalNotes ?? '').trim(), [order]);
  const initialManagerId = useMemo(
    () => order?.managerApprovedById ?? order?.managerId ?? null,
    [order],
  );

  const dirty = useMemo(() => {
    if (!order) {
      return false;
    }

    const approvalChanged = managerApproved !== (order.managerApproved ?? false);
    const notesChanged = normalizedNotes !== initialNotes;
    const managerChanged = (selectedManagerId ?? null) !== (initialManagerId ?? null);

    return approvalChanged || notesChanged || managerChanged;
  }, [order, managerApproved, normalizedNotes, initialNotes, selectedManagerId, initialManagerId]);

  const approvalInfo = useMemo(() => {
    if (!order?.managerApprovedAt) {
      return 'No manager approval recorded yet.';
    }

    const approver = order.managerApprovedBy ?? order.manager ?? null;
    return `Recorded on ${formatDateTime(order.managerApprovedAt)} by ${getUserDisplayName(approver)}.`;
  }, [order]);

  const handleSelectManager = (id: string) => {
    setSelectedManagerId(id);

    const match =
      userResults.find((user) => user.id === id) ??
      (order?.managerApprovedById === id ? order.managerApprovedBy : undefined) ??
      (order?.managerId === id ? order.manager : undefined) ??
      (currentUser?.id === id ? currentUser : undefined) ??
      null;

    setSelectedManager(match ?? null);
    setSearch('');
  };

  const handleClearSelection = () => {
    setSelectedManagerId(null);
    setSelectedManager(null);
    setSearch('');
  };

  const requiresApproverSelection = managerApproved && !selectedManagerId && !lockApproverToCurrentUser;
  const disableSave = !dirty || saving || requiresApproverSelection;

  const handleSave = async () => {
    if (!orderId || !order) {
      return;
    }

    if (managerApproved && !selectedManagerId && !lockApproverToCurrentUser) {
      setSaveError('Select an approver before marking the order as approved.');
      return;
    }

    setSaving(true);
    setSaveError(null);

    const payload: UpdateOrderPayload = {
      managerApproved,
      managerApprovalNotes: normalizedNotes.length ? normalizedNotes : null,
      managerId: (lockApproverToCurrentUser ? currentUser?.id ?? null : selectedManagerId ?? null),
    };

    if (managerApproved) {
      const approverId = lockApproverToCurrentUser ? currentUser?.id : selectedManagerId;
      if (approverId) payload.managerApprovedById = approverId;
    } else if (order.managerApprovedById) {
      payload.managerApprovedById = null;
    }

    try {
      const updated = await updateOrder(orderId, payload);
      setOrder(updated);
      setManagerApproved(updated.managerApproved ?? false);
      setManagerNotes(updated.managerApprovalNotes ?? '');
      const nextManagerId = updated.managerApprovedById ?? updated.managerId ?? null;
      const nextManager = updated.managerApprovedBy ?? updated.manager ?? null;
      setSelectedManagerId(nextManagerId);
      setSelectedManager(nextManager);
      setToast({
        id: Date.now(),
        title: 'Saved',
        message: 'Manager approval has been updated.',
      });
      onCompleted && onCompleted(orderId);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message || 'Failed to update manager approval.'
          : 'Failed to update manager approval.';
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  const approvalBadgeClass = managerApproved
    ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
    : 'bg-slate-200 text-slate-600 border border-slate-300';

  const emptyMessage = search.trim().length < 2
    ? ''
    : searchError ?? 'No users matched your search.';

  return (
    <div className="space-y-6">
      <Notification
        className="flex w-80 max-w-full flex-col items-center gap-1 rounded-xl border border-primary/40 bg-primary/5 px-4 py-3 text-center text-primary shadow-lg"
        options={{
          duration: TOAST_DURATION,
          gravity: 'top',
          position: 'right',
          stopOnFocus: true,
          close: false,
          offset: {
            x: 24,
            y: 24,
          },
        }}
        getRef={(el: NotificationElement) => {
          toastRef.current = el;
        }}
      >
        {toast ? (
          <div className="space-y-1">
            <p className="text-sm font-semibold uppercase tracking-wide text-primary">{toast.title}</p>
            <p className="text-xs text-primary/80">{toast.message}</p>
          </div>
        ) : null}
      </Notification>

      {showHeader ? (
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold text-slate-800">Manager Approval</h2>
          <p className="text-sm text-slate-500">Escalate to a manager for review prior to execution.</p>
        </div>
      ) : null}

      {orderError ? (
        <div className="rounded-xl border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger">{orderError}</div>
      ) : null}

      <div className="rounded-2xl  bg-white p-6 shadow-sm">
        {loadingOrder ? (
          <p className="text-sm text-slate-500">Loading order details…</p>
        ) : !order ? (
          <p className="text-sm text-slate-500">Select an order to manage manager approvals.</p>
        ) : (
          <div className="space-y-6">
            {/* Top Row: Order Number + Status + Clear Button */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              {/* Left: Order number */}
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">Order number</p>
                <p className="text-lg font-semibold text-slate-800">{order.orderNumber}</p>
              </div>

              {/* Right: Status and Clear Selection */}
              <div className="flex items-center gap-3">
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${approvalBadgeClass}`}
                >
                  {managerApproved ? 'Approved' : 'Pending'}
                  {dirty ? ' • Unsaved' : ''}
                </span>

                {!lockApproverToCurrentUser && selectedManagerId ? (
                  <button
                    type="button"
                    className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-600 transition hover:border-danger/60 hover:text-danger disabled:opacity-50"
                    onClick={handleClearSelection}
                    disabled={saving}
                  >
                    Clear selection
                  </button>
                ) : null}
              </div>
            </div>

            {/* AI summary snippet (if available) */}
            {order && (order as any).aiAnalysis ? (() => {
              const analysis = (order as any).aiAnalysis;
              const scores = (analysis as any).scores ?? {};
              const overall = scores.overall ?? {};
              const styles = getAiHealthScoreStyles(overall.score);
              return (
                <div className={`flex flex-col gap-2 rounded-2xl border p-4 text-sm md:flex-row md:items-center md:justify-between ${styles.container}`}>
                  <div>
                    <p className={`text-xs font-semibold uppercase tracking-wide ${styles.label}`}>
                      AI vehicle health summary
                    </p>
                    <p className={`mt-1 text-sm ${styles.text}`}>
                      <span className="font-semibold">
                        {overall.score ?? '—'}
                      </span>{' '}
                      <span className={`text-xs uppercase tracking-wide ${styles.label}`}>
                        {overall.label ?? ''}
                      </span>
                      {overall.summary ? (
                        <span className={`ml-1 ${styles.label}`}>
                          — {overall.summary}
                        </span>
                      ) : null}
                    </p>
                  </div>
                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-lg border border-indigo-500 px-3 py-1.5 text-xs font-semibold text-indigo-600 transition hover:bg-indigo-100"
                      onClick={() => setAiModalOpen(true)}
                    >
                      View AI analysis
                    </button>
                  </div>
                </div>
              );
            })() : null}

            {lockApproverToCurrentUser ? null : (
              <div className="space-y-3">
                <div>
                  <p className="mt-1 mb-2 text-xs text-slate-500">Search by name, username, email, or staff ID. The dropdown highlights the selected approver.</p>
                </div>

                <SearchableDropdown
                  searchValue={search}
                  onSearchChange={setSearch}
                  options={options}
                  selectedValue={selectedManagerId ?? undefined}
                  onSelect={handleSelectManager}
                  loading={searching}
                  placeholder="Type to approval manager"
                  emptyMessage={emptyMessage}
                />

              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 mb-2">
              {lockApproverToCurrentUser ? (
                <div className="space-y-1 text-sm text-slate-600">
                  <p className="font-semibold text-slate-800">{getUserDisplayName(currentUser)}</p>
                  {currentUser?.username ? (
                    <p className="text-xs text-slate-500">Username: {currentUser.username}</p>
                  ) : null}
                  {currentUser?.email ? (
                    <p className="text-xs text-slate-500">Email: {currentUser.email}</p>
                  ) : null}
                  {currentUser?.mobileNumber ? (
                    <p className="text-xs text-slate-500">Mobile: {currentUser.mobileNumber}</p>
                  ) : null}
                </div>
              ) : selectedManager ? (
                <div className="space-y-1 text-sm text-slate-600">
                  <p className="font-semibold text-slate-800">{getUserDisplayName(selectedManager)}</p>
                  <p className="text-xs text-slate-500">Username: {selectedManager.username}</p>
                  {selectedManager.email ? (
                    <p className="text-xs text-slate-500">Email: {selectedManager.email}</p>
                  ) : null}
                  {selectedManager.mobileNumber ? (
                    <p className="text-xs text-slate-500">Mobile: {selectedManager.mobileNumber}</p>
                  ) : null}
                  <p className="text-xs text-slate-500">Locations: {formatUserLocations(selectedManager)}</p>
                </div>
              ) : (
                <p className="text-xs text-slate-500">No approver selected yet.</p>
              )}
            </div>

            <div>
              <label className="inline-flex items-center gap-3 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-primary"
                  checked={managerApproved}
                  onChange={(event) => setManagerApproved(event.target.checked)}
                  disabled={saving}
                />
                {lockApproverToCurrentUser ? 'Approve order' : 'Manager approval completed'}
              </label>
              {lockApproverToCurrentUser ? null : (
                <p className="mt-1 text-xs text-slate-500">
                  Toggle this once the chosen approver has reviewed and confirmed the order.
                </p>
              )}
              {requiresApproverSelection ? (
                <p className="mt-1 text-xs text-amber-600">Select an approver to record the approval.</p>
              ) : null}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Approval notes</label>
              <textarea
                value={managerNotes}
                onChange={(event) => setManagerNotes(event.target.value)}
                placeholder="Capture any remarks shared during the approval."
                rows={4}
                className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-60"
                disabled={saving}
              />
              <p className="mt-1 text-xs text-slate-400">These notes are visible to the workshop team for reference.</p>
            </div>

            <div
              className={`rounded-xl border px-4 py-3 text-xs mb-2 ${
                order?.managerApproved
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                  : 'border-slate-200 bg-slate-50 text-slate-500'
              }`}
            >
              <p>{approvalInfo}</p>
              {dirty ? (
                <p className="mt-1 text-amber-600">
                  You have unsaved changes. Save to update the approval log.
                </p>
              ) : null}
            </div>

            {saveError ? (
              <div className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">{saveError}</div>
            ) : null}

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-white shadow transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleSave}
                disabled={disableSave}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* AI analysis modal */}
      {aiModalOpen && order && (order as any).aiAnalysis ? (() => {
        const analysis = (order as any).aiAnalysis;
        const scores = (analysis as any).scores ?? {};
        const overall = scores.overall ?? {};
        const sections = scores.sections ?? {};
        const recs = (analysis as any).recommendations ?? {};
        const now = (recs.recommendedNow?.products ?? []) as Array<{
          id: string;
          name: string;
          reason: string;
        }>;
        const future = (recs.recommendedFuture?.products ?? []) as Array<{
          id: string;
          name: string;
          reason: string;
        }>;
        const sectionKeys: Array<keyof typeof sections> = [
          'tyres',
          'engine',
          'brakes',
          'suspension',
          'electrical',
          'body',
        ];
        return (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 px-4">
            <div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">
                  AI recommendations
                </h2>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 text-slate-500 hover:bg-slate-100"
                  onClick={() => setAiModalOpen(false)}
                >
                  ✕
                </button>
              </div>
              <div className="max-h-[70vh] overflow-y-auto pr-1 text-sm text-slate-700">
                <div className="space-y-4">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <h3 className="text-sm font-semibold text-slate-900">
                      Overall health
                    </h3>
                    {(() => {
                      const styles = getAiHealthScoreStyles(overall.score);
                      return (
                        <>
                          <p className={`mt-1 text-sm ${styles.text}`}>
                            <span className="font-semibold">
                              {overall.score ?? '—'}
                            </span>{' '}
                            <span
                              className={`uppercase text-xs tracking-wide ${styles.label}`}
                            >
                              {overall.label ?? ''}
                            </span>
                          </p>
                          {overall.summary ? (
                            <p className={`mt-2 text-sm ${styles.text}`}>
                              {overall.summary}
                            </p>
                          ) : null}
                        </>
                      );
                    })()}
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">
                      Section scores
                    </h3>
                    <div className="mt-2 grid gap-3 md:grid-cols-2">
                      {sectionKeys.map((key) => {
                        const keyName = String(key);
                        const sec = (sections as any)[keyName];
                        if (!sec) return null;
                        const secStyles = getAiHealthScoreStyles(sec.score);
                        return (
                          <div
                            key={keyName}
                            className={`rounded-lg border p-3 ${secStyles.container}`}
                          >
                            <div className="flex items-center justify-between text-sm">
                              <span className={`font-semibold ${secStyles.text}`}>
                                {keyName.replace(/^\w/, (c) => c.toUpperCase())}
                              </span>
                              <span className={`text-xs font-medium ${secStyles.label}`}>
                                {sec.score ?? '—'} • {sec.label ?? ''}
                              </span>
                            </div>
                            {sec.summary ? (
                              <p className={`mt-1 text-xs ${secStyles.text}`}>
                                {sec.summary}
                              </p>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">
                      Recommended now
                    </h3>
                    {now.length === 0 ? (
                      <p className="mt-1 text-sm text-slate-600">
                        No immediate actions.
                      </p>
                    ) : (
                      <ul className="mt-2 space-y-2 text-sm text-slate-700">
                        {now.map((item) => (
                          <li
                            key={item.id}
                            className="rounded-md bg-emerald-50 p-2"
                          >
                            <div className="font-semibold text-emerald-900">
                              {item.name}
                            </div>
                            <div className="text-xs text-emerald-800">
                              {item.reason}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">
                      Recommended in future
                    </h3>
                    {future.length === 0 ? (
                      <p className="mt-1 text-sm text-slate-600">
                        No future recommendations.
                      </p>
                    ) : (
                      <ul className="mt-2 space-y-2 text-sm text-slate-700">
                        {future.map((item) => (
                          <li
                            key={item.id}
                            className="rounded-md bg-slate-50 p-2"
                          >
                            <div className="font-semibold text-slate-900">
                              {item.name}
                            </div>
                            <div className="text-xs text-slate-800">
                              {item.reason}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })() : null}
    </div>
  );
};

export default OrderManagerApproval;
